import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useCallback } from 'react';
import PT from 'prop-types';
import { Transition } from 'react-transition-group';
import { O as Overlay } from './Overlay-DmOIupoz.js';
import { a as TaktIdProvider } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';
import { P as Portal } from '../Portal/Portal-DPcnZ8IV.js';

/**
 * Expose all the ContextualDrawer Props and the passthrough props of the `<div>` element
 */

/*
 * ContextualDrawerComponent is a workaround for the types generated by forwardRef() when wrapping a type
 * created by MergeStyledComponentElementProps. By default, the type generated will have all the known
 * attributes of the HTML listed out. This creates a problem for older version of the lib.dom.d.ts that
 * ships with typescript because it will be missing any new attributes. e.g. the `nonce` attribute
 * was added TS 3.2.0, so earlier versions of TS do not recognize it as optional.
 */

const ContextualDrawer = /*#__PURE__*/forwardRef(({
  children,
  closeButtonLabel,
  closeButtonProps,
  isOpen,
  onClose,
  withCloseButton,
  taktId,
  taktValue,
  ...rest
}, ref) => {
  const drawerContainerRef = useRef(null);
  const handleCloseButtonClicked = useCallback(event => {
    if (onClose) {
      onClose(event);
    }
  }, [onClose]);
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('contextual-drawer')
  }, /*#__PURE__*/React__default.createElement(Transition, {
    timeout: 150,
    appear: true,
    in: isOpen,
    nodeRef: drawerContainerRef
  }, slideState => {
    if (slideState === 'exited') return null;
    return /*#__PURE__*/React__default.createElement(Portal, null, /*#__PURE__*/React__default.createElement(Overlay, _extends({}, rest, {
      ref: ref,
      drawerContainerRef: drawerContainerRef,
      slideState: slideState,
      withCloseButton: withCloseButton,
      closeButtonLabel: closeButtonLabel,
      handleCloseButtonClicked: handleCloseButtonClicked,
      closeButtonProps: closeButtonProps
    }), children));
  }));
});
ContextualDrawer.propTypes = {
  /**
   * The contents of the Drawer.
   */
  children: PT.node.isRequired,
  /**
   * Customized text label for close button.
   */
  closeButtonLabel: PT.string,
  /**
   * Props that are applied to the CloseButton component.
   */
  closeButtonProps: PT.objectOf(PT.any),
  /**
   * The state of the drawer. If true, the drawer will be open, otherwise it will be closed.
   */
  isOpen: PT.bool,
  /**
   * Called when a user presses the close button.
   */
  onClose: PT.func,
  /**
   * Conditionally renders the close button. If true, the close button will be rendered,
   * otherwise it will not be rendered.
   */
  withCloseButton: PT.bool
};
ContextualDrawer.defaultProps = {
  closeButtonLabel: '',
  closeButtonProps: undefined,
  isOpen: false,
  onClose: () => undefined,
  withCloseButton: true
};
ContextualDrawer.displayName = 'ContextualDrawer';

export { ContextualDrawer as C };
//# sourceMappingURL=ContextualDrawer-C-yIwj6m.js.map
